import { haversineMeters } from './distance';

/**
 * Lógica del mapa de tiendas: agrupar los marcados por PDV, decir cuáles cayeron fuera de la
 * geocerca y proponer una coordenada cuando la tienda todavía no tiene una.
 *
 * Va aparte de la pantalla a propósito: acá vive la decisión de si un marcado está "fuera", que
 * es la misma que toma el servidor para poner `lejos_del_pdv`. Si la pantalla la calculara por
 * su cuenta podría pintar de rojo un punto que el servidor aceptó, y el mapa dejaría de valer
 * como explicación de las alertas.
 */

export interface PuntoTienda {
  id: string;
  code: string;
  name: string;
  lat: number | null;
  lng: number | null;
  radius_m: number;
  location_source: string;
}

export interface PuntoMarcado {
  id: string;
  pdv_id: string;
  lat: number;
  lng: number;
}

export interface ResumenTienda {
  dentro: number;
  fuera: number;
  /** Distancia del marcado más lejano al centro de la tienda, en metros enteros. */
  maxDistanciaM: number | null;
}

/**
 * ¿Este marcado quedó fuera de la geocerca?
 *
 * Sin coordenada de tienda devuelve `false`, igual que el disparador del servidor, que solo
 * compara cuando `pdv.lat` no es nulo. Pintar de rojo lo que el servidor nunca evaluó sería
 * inventar una alerta que no existe en los datos.
 */
export function estaFuera(tienda: PuntoTienda, marcado: PuntoMarcado): boolean {
  if (tienda.lat === null || tienda.lng === null) return false;
  return haversineMeters(marcado.lat, marcado.lng, tienda.lat, tienda.lng) > tienda.radius_m;
}

/** Cuántos marcados de esta tienda cayeron dentro y cuántos fuera, y cuál fue el más lejano. */
export function resumirTienda(tienda: PuntoTienda, marcados: PuntoMarcado[]): ResumenTienda {
  const propios = marcados.filter((m) => m.pdv_id === tienda.id);
  if (tienda.lat === null || tienda.lng === null) {
    return { dentro: 0, fuera: 0, maxDistanciaM: null };
  }
  let dentro = 0;
  let fuera = 0;
  let max = 0;
  for (const m of propios) {
    const d = haversineMeters(m.lat, m.lng, tienda.lat, tienda.lng);
    if (d > max) max = d;
    if (d > tienda.radius_m) fuera += 1;
    else dentro += 1;
  }
  return {
    dentro,
    fuera,
    maxDistanciaM: propios.length === 0 ? null : Math.round(max),
  };
}

export interface CoordenadaSugerida {
  lat: number;
  lng: number;
  /** Marcados que sostienen la propuesta (los descartados por dispersos no cuentan). */
  usados: number;
  descartados: number;
  /** Qué tan juntos están los puntos USADOS: el más lejano de ellos al centro propuesto. */
  dispersionM: number;
  /**
   * El marcado más lejano de TODOS al centro propuesto, incluidos los descartados.
   *
   * Existe porque `dispersionM` sola engaña: mide solo los puntos que sobrevivieron al filtro,
   * así que una propuesta que se quedó con UNO de tres reporta "0 m" y parece milimétrica
   * cuando en realidad dos marcados la contradicen. Lo encontró una prueba con tres puntos
   * repartidos a kilómetros: el promedio cayó sobre el del medio, los otros dos se
   * descartaron, y el resultado se anunciaba como perfecto. Con este número al lado, el
   * desacuerdo se ve.
   */
  desacuerdoM: number;
}

/** Cuán lejos del primer promedio puede estar un punto para seguir contando, en metros. */
const TOLERANCIA_M = 100;

/**
 * Propone la coordenada de una tienda a partir de los marcados que se hicieron ahí.
 *
 * CÓMO: promedio en dos pasadas. La primera con todos los puntos; después se descartan los que
 * quedan a más de 100 m de ese promedio y se recalcula con el resto.
 *
 * POR QUÉ EN DOS PASADAS: un solo marcado hecho desde la vereda de enfrente —o desde la casa,
 * antes de entrar— arrastra el promedio decenas de metros hacia un lugar donde la tienda no
 * está, y ese error se paga después como alertas falsas para todo el mundo. Pasó de verdad con
 * Makro Independencia: tres marcados juntos y uno a 249 m; promediando los cuatro, el centro
 * quedaba a mitad de camino de ningún lado. La mayoría manda sobre el punto suelto.
 *
 * Devuelve `null` sin marcados. Con uno solo devuelve ese punto, con `dispersionM = 0`: es una
 * propuesta débil, y quien mire el número tiene que poder notarlo.
 */
export function sugerirCoordenada(marcados: PuntoMarcado[]): CoordenadaSugerida | null {
  if (marcados.length === 0) return null;

  const promedio = (puntos: PuntoMarcado[]) => ({
    lat: puntos.reduce((s, p) => s + p.lat, 0) / puntos.length,
    lng: puntos.reduce((s, p) => s + p.lng, 0) / puntos.length,
  });

  const centro1 = promedio(marcados);
  const cercanos = marcados.filter(
    (m) => haversineMeters(m.lat, m.lng, centro1.lat, centro1.lng) <= TOLERANCIA_M,
  );
  // Si TODOS quedaron lejos del promedio, los puntos están repartidos y no hay mayoría que
  // mande: se devuelve el promedio de todos, con la dispersión a la vista para que se note.
  const usados = cercanos.length > 0 ? cercanos : marcados;
  const centro = promedio(usados);

  const alCentro = (lista: PuntoMarcado[]) =>
    lista.reduce((max, m) => Math.max(max, haversineMeters(m.lat, m.lng, centro.lat, centro.lng)), 0);

  return {
    lat: centro.lat,
    lng: centro.lng,
    usados: usados.length,
    descartados: marcados.length - usados.length,
    dispersionM: Math.round(alCentro(usados)),
    desacuerdoM: Math.round(alCentro(marcados)),
  };
}
